import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  ariaLabel?: string;
  className?: string;
  children: React.ReactNode;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  ariaLabel = 'Dialog',
  className = '',
  children,
}) => {
  const [canDrag, setCanDrag] = useState(false);
  const scrollYRef = useRef(0);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const portalEl = useMemo(() => {
    if (typeof document === 'undefined') return null;
    const el = document.createElement('div');
    el.setAttribute('data-bottom-sheet-portal', 'true');
    return el;
  }, []);

  useEffect(() => {
    if (!portalEl) return;
    document.body.appendChild(portalEl);
    return () => {
      try {
        document.body.removeChild(portalEl);
      } catch {}
    };
  }, [portalEl]);

  const enableDrag = (e: React.PointerEvent) => {
    setCanDrag(true);
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  useEffect(() => {
    if (!canDrag) return;
    const off = () => setCanDrag(false);
    window.addEventListener('pointerup', off);
    window.addEventListener('pointercancel', off);
    return () => {
      window.removeEventListener('pointerup', off);
      window.removeEventListener('pointercancel', off);
    };
  }, [canDrag]);

  useEffect(() => {
    if (!isOpen) return;

    const htmlEl = document.documentElement;
    const bodyEl = document.body;
    const navEl = document.querySelector<HTMLElement>('.bottom-nav-fixed') ||
                  document.querySelector<HTMLElement>('.bottom-nav');

    scrollYRef.current = window.scrollY || window.pageYOffset || 0;

    htmlEl.classList.add('hide-bottom-nav');
    bodyEl.classList.add('modal-open', 'hide-bottom-nav');

    if (navEl) {
      (navEl as any).dataset.prevDisplay = navEl.style.display || '';
      navEl.style.display = 'none';
      navEl.setAttribute('inert', '');
    }

    bodyEl.style.position = 'fixed';
    bodyEl.style.top = `-${scrollYRef.current}px`;
    bodyEl.style.left = '0';
    bodyEl.style.right = '0';
    bodyEl.style.width = '100%';
    bodyEl.style.overflow = 'hidden';

    const guardScroll = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) {
        e.preventDefault();
        return;
      }
      if (target.closest('[data-modal-scroll]')) return;
      e.preventDefault();
    };

    document.addEventListener('touchmove', guardScroll, { passive: false });
    document.addEventListener('wheel', guardScroll, { passive: false });

    return () => {
      bodyEl.style.position = '';
      bodyEl.style.top = '';
      bodyEl.style.left = '';
      bodyEl.style.right = '';
      bodyEl.style.width = '';
      bodyEl.style.overflow = '';

      htmlEl.classList.remove('hide-bottom-nav');
      bodyEl.classList.remove('modal-open', 'hide-bottom-nav');

      if (navEl) {
        navEl.style.display = (navEl as any).dataset?.prevDisplay || '';
        navEl.removeAttribute('inert');
        delete (navEl as any).dataset?.prevDisplay;
      }

      window.scrollTo(0, scrollYRef.current || 0);

      document.removeEventListener('touchmove', guardScroll as any);
      document.removeEventListener('wheel', guardScroll as any);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen || !portalEl) return null;

  const modal = (
    <AnimatePresence mode="wait">
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 24, stiffness: 300 }}
          className={`w-full max-w-md bg-white rounded-t-3xl shadow-2xl flex flex-col ${className}`}
          style={{
            maxHeight: '96dvh',
            paddingBottom: '0px',
          }}
          onClick={(e) => e.stopPropagation()}
          drag={canDrag ? 'y' : false}
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={0.04}
          dragMomentum={false}
          onDragEnd={(_, info) => {
            if (info.offset.y > 120) onClose();
          }}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
        >
          <div
            className="w-full flex justify-center py-2"
            onPointerDown={enableDrag}
          >
            <div className="h-1.5 w-12 rounded-full bg-gray-300" />
          </div>

          <div
            data-modal-scroll
            className="flex-1 overflow-y-auto overscroll-contain"
            style={{
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
              overscrollBehavior: 'contain',
            }}
          >
            {children}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );

  return createPortal(modal, portalEl);
};
